/**
 * Janelinha flutuante da chamada de voz (saída e entrante) — NÃO é modal:
 * sem fundo escurecido e sem travar o painel. Com a ligação no ar o atendente
 * segue respondendo o cliente, trocando de conversa e navegando (pedido do
 * dono: "igual ao chat interno"). Estados: pedindo mic → chamando → tocando →
 * ativa (timer) → encerrada/erro.
 *
 * Arrasta-se pelo corpo do card (botões não), mesmo padrão do chat interno
 * (InternalChatPopup). Nasce no canto superior direito — onde aparece o card
 * da chamada entrante, então atender não "pula" a janela de lugar. A posição
 * arrastada vale enquanto a janela está aberta; fechar reseta.
 */
import { Mic, MicOff, Phone, PhoneOff } from 'lucide-react';
import { useRef } from 'react';
import type { VoipCall } from './useVoipCall';

function fmtElapsed(s: number): string {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

const STATE_LABEL: Record<string, string> = {
  requesting: 'Preparando microfone…',
  calling: 'Iniciando chamada…',
  ringing: 'Chamando…',
  active: 'Em chamada',
  ended: 'Chamada encerrada',
  error: 'Falha na chamada',
};

/** Ligação no ar com o outro lado — encerrar precisa avisar (botão vermelho). */
export function isCallConnected(state: VoipCall['state']): boolean {
  return state === 'active' || state === 'ringing' || state === 'calling';
}

export interface CallWindowPos {
  x: number;
  y: number;
}

export function CallWindow({
  call,
  contactName,
  answering = false,
  pos,
  onMove,
  onClose,
}: {
  call: VoipCall;
  contactName: string;
  /** true = atendendo chamada entrante (rótulo "Conectando…"). */
  answering?: boolean;
  /** Posição arrastada (px, fixed). null = ancorada no canto sup. direito. */
  pos: CallWindowPos | null;
  onMove: (pos: CallWindowPos) => void;
  /** Encerrar (se no ar) e fechar a janela — quem decide é a sessão. */
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const inCall = isCallConnected(call.state);

  // Mesmo arrasto do chat interno: mouse-down fora de botão inicia; solta em
  // qualquer lugar. Limitado à tela (8px de margem). preventDefault evita
  // selecionar texto durante o arrasto.
  const onPanelMouseDown = (e: React.MouseEvent): void => {
    const target = e.target as HTMLElement;
    if (target.closest('button')) return;
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const dx = e.clientX - rect.left;
    const dy = e.clientY - rect.top;
    const onMoveEv = (ev: MouseEvent): void => {
      const x = Math.min(Math.max(8, ev.clientX - dx), window.innerWidth - panel.offsetWidth - 8);
      const y = Math.min(Math.max(8, ev.clientY - dy), window.innerHeight - panel.offsetHeight - 8);
      onMove({ x, y });
    };
    const onUp = (): void => {
      window.removeEventListener('mousemove', onMoveEv);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMoveEv);
    window.addEventListener('mouseup', onUp);
    e.preventDefault();
  };

  const label =
    call.state === 'active'
      ? fmtElapsed(call.elapsed)
      : call.state === 'ended'
        ? (call.endLabel ?? 'Chamada encerrada')
        : answering && (call.state === 'calling' || call.state === 'requesting')
          ? 'Conectando…'
          : (STATE_LABEL[call.state] ?? call.state);

  return (
    <div
      ref={panelRef}
      onMouseDown={onPanelMouseDown}
      role="dialog"
      aria-label={`Chamada com ${contactName}`}
      className={`fixed z-[100] w-[300px] cursor-move select-none rounded-md bg-n-0 p-4 shadow-lg ring-1 ring-n-200 ${
        pos ? '' : 'top-2 right-4'
      }`}
      style={pos ? { left: pos.x, top: pos.y } : undefined}
    >
      <div className="flex items-center gap-3">
        <div
          className={
            call.state === 'active'
              ? 'flex h-11 w-11 shrink-0 items-center justify-center rounded-pill bg-success-50 text-success-600'
              : 'flex h-11 w-11 shrink-0 items-center justify-center rounded-pill bg-brand-50 text-brand-600'
          }
        >
          <Phone size={20} className={call.state === 'ringing' ? 'animate-pulse' : ''} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-bold text-[14px] text-n-900">{contactName}</div>
          <div className="truncate text-[12px] text-n-500 tabular-nums">{label}</div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {call.state === 'active' && (
            <button
              type="button"
              onClick={call.toggleMute}
              className={
                call.muted
                  ? 'flex h-9 w-9 cursor-pointer items-center justify-center rounded-pill bg-danger-50 text-danger-600 transition-colors hover:bg-danger-100'
                  : 'flex h-9 w-9 cursor-pointer items-center justify-center rounded-pill bg-n-100 text-n-700 transition-colors hover:bg-n-200'
              }
              aria-label={call.muted ? 'Reativar microfone' : 'Silenciar microfone'}
              title={call.muted ? 'Reativar microfone' : 'Silenciar microfone'}
            >
              {call.muted ? <MicOff size={16} /> : <Mic size={16} />}
            </button>
          )}
          {inCall ? (
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-pill bg-danger-500 text-white transition-colors hover:bg-danger-600"
              aria-label="Encerrar chamada"
              title="Encerrar chamada"
            >
              <PhoneOff size={16} />
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="h-9 cursor-pointer rounded-md bg-n-100 px-3 font-semibold text-[13px] text-n-700 transition-colors hover:bg-n-200"
            >
              Fechar
            </button>
          )}
        </div>
      </div>
      {call.error && <div className="mt-2 text-[12px] text-danger-600">{call.error}</div>}
    </div>
  );
}
